/**
 * MuiProductsList - Optimized Products List Page
 *
 * - No products loaded on mount — search-driven
 * - Debounced live search (400ms)
 * - Filters auto-apply on click
 * - Server-side pagination (50 per page)
 * - Analytics lazy-loaded on expand
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  Box,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  IconButton,
  CircularProgress,
  Stack,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Collapse,
  Grid,
} from "@mui/material"
import { Link as RouterLink } from "react-router-dom"
import SearchIcon from "@mui/icons-material/Search"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import RefreshIcon from "@mui/icons-material/Refresh"
import InventoryIcon from "@mui/icons-material/Inventory"
import CategoryIcon from "@mui/icons-material/Category"
import HomeIcon from "@mui/icons-material/Home"
import BuildIcon from "@mui/icons-material/Build"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing"
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices"
import LayersIcon from "@mui/icons-material/Layers"
import BarChartIcon from "@mui/icons-material/BarChart"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import ClearIcon from "@mui/icons-material/Clear"
import ViewModuleIcon from "@mui/icons-material/ViewModule"
import CountertopsIcon from "@mui/icons-material/Countertops"
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom"
import StarIcon from "@mui/icons-material/Star"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from "recharts"

import { getProductsPaginated, fetchPaginated } from "helpers/api_helper"
import { GET_PRODUCTS } from "helpers/api_urls"
import BoxDiagram from "../Store/BoxDiagram"

// Top-level category groups
const CATEGORY_GROUPS = [
  { key: "ALL", label: "All Products", filter: null, icon: ViewModuleIcon },
  { key: "BOXES", label: "Boxes", filter: "(product_type=B)", icon: InventoryIcon },
  { key: "ACCESSORIES", label: "Accessories", filter: "((category like %ΑΞΕΣΟΥΑΡ%) or (category like %Accessories%))", icon: BuildIcon },
  { key: "SPECIAL", label: "Special", filter: "((category like %Special%) or (category like %ΕΙΔΙΚΑ%))", icon: StarIcon },
  { key: "PAGKOS", label: "Pagkos", filter: "((category like %Pagkos%) or (category like %ΠΑΓΚΟΥ%))", icon: CountertopsIcon },
  { key: "DOORS", label: "Doors", filter: "(category like %ΠΟΡΤΑ%)", icon: MeetingRoomIcon },
  { key: "PANELS", label: "Panels", filter: "(category like %ΕΠΕΝΔΥΣΗ%)", icon: LayersIcon },
  { key: "DECORATIVE", label: "Decorative", filter: "(category like %ΔΙΑΚΟΣΜΗΤΙΚΑ%)", icon: MiscellaneousServicesIcon },
  { key: "PURCHASED", label: "Purchased Materials", filter: "(usageType=PURCHASED-MATERIAL)", icon: PrecisionManufacturingIcon },
]

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return "€0.00"
  return new Intl.NumberFormat("el-GR", { style: "currency", currency: "EUR" }).format(amount)
}

// Lightweight analytics — only fields needed for counting
async function fetchProductAnalytics() {
  try {
    const result = await fetchPaginated(GET_PRODUCTS, {
      limit: 5000,
      offset: 0,
      fields: "id,category,product_type,usageType,stock,salesprice",
    })
    const products = result.data || []
    const catCounts = {}
    const positionCounts = { "ΒΑΣΕΩΣ": 0, "ΚΡΕΜΑΣΤΟ": 0, "ΟΛΟΣΩΜΟ": 0 }
    const accessoryCounts = {}
    const groupTotals = { Boxes: 0, Accessories: 0, Special: 0, Pagkos: 0, Doors: 0, Panels: 0, Decorative: 0, Other: 0 }
    let inStock = 0, lowStock = 0, outOfStock = 0, totalValue = 0

    products.forEach((p) => {
      const stock = Number(p.stock) || 0
      const price = Number(p.salesprice) || 0
      if (stock > 0) inStock++
      if (stock > 0 && stock < 10) lowStock++
      if (stock <= 0) outOfStock++
      totalValue += price * stock

      let cats = []
      if (p.category) {
        try {
          const parsed = JSON.parse(p.category)
          if (Array.isArray(parsed)) cats = parsed
        } catch { /* skip */ }
      }
      cats.forEach(cat => {
        if (cat && cat !== "Uncategorized") catCounts[cat] = (catCounts[cat] || 0) + 1
      })

      // Position counts
      if (cats.includes("ΒΑΣΕΩΣ")) positionCounts["ΒΑΣΕΩΣ"]++
      if (cats.includes("ΚΡΕΜΑΣΤΟ")) positionCounts["ΚΡΕΜΑΣΤΟ"]++
      if (cats.includes("ΟΛΟΣΩΜΟ")) positionCounts["ΟΛΟΣΩΜΟ"]++

      // Accessory sub-categories
      const isAccessory = cats.some(c => c.includes("ΑΞΕΣΟΥΑΡ") || c === "Accessories")
      if (isAccessory) {
        cats.forEach(cat => {
          if (cat !== "Uncategorized") accessoryCounts[cat] = (accessoryCounts[cat] || 0) + 1
        })
      }

      // Group totals for pie chart
      let grouped = false
      if (p.product_type === "B" || cats.includes("ΒΑΣΕΩΣ") || cats.includes("ΚΡΕΜΑΣΤΟ") || cats.includes("ΟΛΟΣΩΜΟ")) {
        groupTotals.Boxes++; grouped = true
      }
      if (isAccessory) { groupTotals.Accessories++; grouped = true }
      if (cats.some(c => c.includes("ΠΟΡΤΑ"))) { groupTotals.Doors++; grouped = true }
      if (cats.some(c => c.includes("ΕΠΕΝΔΥΣΗ"))) { groupTotals.Panels++; grouped = true }
      if (cats.includes("ΠΑΓΚΟΥ") || cats.includes("Pagkos")) { groupTotals.Pagkos++; grouped = true }
      if (cats.includes("Special")) { groupTotals.Special = (groupTotals.Special || 0) + 1; grouped = true }
      if (cats.includes("ΔΙΑΚΟΣΜΗΤΙΚΑ")) { groupTotals.Decorative++; grouped = true }
      if (!grouped) groupTotals.Other++
    })

    return {
      categories: catCounts,
      positionCounts,
      accessoryCounts,
      groupTotals,
      stats: { total: result.total || 0, inStock, lowStock, outOfStock, totalValue },
    }
  } catch {
    return { categories: {}, positionCounts: {}, accessoryCounts: {}, groupTotals: {}, stats: { total: 0, inStock: 0, lowStock: 0, outOfStock: 0, totalValue: 0 } }
  }
}

const MuiProductsList = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  document.title = t("Products") + " | FlexFlow ERP"

  // Search & filter state
  const [searchInput, setSearchInput] = useState("")
  const [positionFilter, setPositionFilter] = useState(null)
  const [golaFilter, setGolaFilter] = useState(false)
  const [openingFilter, setOpeningFilter] = useState(null)
  const [categoryGroupFilter, setCategoryGroupFilter] = useState(null)

  // Data state
  const [products, setProducts] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [hasSearched, setHasSearched] = useState(false)

  // Analytics state
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false)
  const [analyticsData, setAnalyticsData] = useState({ categories: {}, usageTypes: {}, stats: {} })
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  const [analyticsFetched, setAnalyticsFetched] = useState(false)

  // Refs for debounce and race conditions
  const debounceRef = useRef(null)
  const fetchIdRef = useRef(0)

  // ─── Fetch products ──────────────────────────────────────────
  const fetchProducts = useCallback(async (search, pos, gola, opening, catGroup, pg, sz) => {
    const currentId = ++fetchIdRef.current
    setLoading(true)

    try {
      const filters = []
      // Text search — build OR filter directly (don't use fetchPaginated's search
      // param which wraps OR in double-parens that the backend silently ignores)
      if (search && search.trim()) {
        const s = search.trim()
        filters.push(`(sku like %${s}%) OR (name like %${s}%)`)
      }
      if (catGroup) {
        const group = CATEGORY_GROUPS.find(g => g.key === catGroup)
        if (group && group.filter) filters.push(group.filter)
      }
      if (pos) filters.push(`(cabinet_position=${pos})`)
      if (gola) filters.push(`(category like %,"GOLA"%)`)
      if (opening) filters.push(`(cabinet_opening=${opening})`)
      const filter = filters.length > 0 ? filters.join(" AND ") : null

      const result = await fetchPaginated(GET_PRODUCTS, {
        limit: sz,
        offset: pg * sz,
        filter,
        orderBy: "name",
        orderDir: "ASC",
      })

      if (currentId === fetchIdRef.current) {
        setProducts(result.data || [])
        setTotal(result.total || 0)
        setHasSearched(true)
      }
    } catch (err) {
      if (currentId === fetchIdRef.current) {
        console.error("Product fetch error:", err)
        setProducts([])
        setTotal(0)
      }
    } finally {
      if (currentId === fetchIdRef.current) setLoading(false)
    }
  }, [])

  // Cleanup debounce on unmount
  useEffect(() => () => clearTimeout(debounceRef.current), [])

  // ─── Helper: fire fetch immediately (for filters, page changes) ──
  const fetchNow = useCallback((search, pos, gola, opening, catGroup, pg = 0, sz = pageSize) => {
    clearTimeout(debounceRef.current)
    setPage(pg)
    fetchProducts(search, pos, gola, opening, catGroup, pg, sz)
  }, [fetchProducts, pageSize])

  // ─── Search input change (debounced 400ms) ─────────────────────
  const handleSearchInput = useCallback((e) => {
    const val = e.target.value
    setSearchInput(val)
    clearTimeout(debounceRef.current)
    if (val.length >= 2) {
      debounceRef.current = setTimeout(() => {
        setPage(0)
        fetchProducts(val, positionFilter, golaFilter, openingFilter, categoryGroupFilter, 0, pageSize)
      }, 400)
    } else if (val.length === 0 && (categoryGroupFilter || positionFilter || golaFilter || openingFilter)) {
      // Cleared search but has active filters — refetch without search
      setPage(0)
      fetchProducts("", positionFilter, golaFilter, openingFilter, categoryGroupFilter, 0, pageSize)
    }
  }, [fetchProducts, positionFilter, golaFilter, openingFilter, categoryGroupFilter, pageSize])

  // ─── Filter changes (immediate) ──────────────────────────────
  const handlePositionFilter = useCallback((pos) => {
    const newPos = positionFilter === pos ? null : pos
    setPositionFilter(newPos)
    fetchNow(searchInput, newPos, golaFilter, openingFilter, categoryGroupFilter, 0)
  }, [positionFilter, searchInput, golaFilter, openingFilter, categoryGroupFilter, fetchNow])

  const handleGolaFilter = useCallback(() => {
    const newGola = !golaFilter
    setGolaFilter(newGola)
    fetchNow(searchInput, positionFilter, newGola, openingFilter, categoryGroupFilter, 0)
  }, [golaFilter, searchInput, positionFilter, openingFilter, categoryGroupFilter, fetchNow])

  const handleOpeningFilter = useCallback((type) => {
    const newOpening = openingFilter === type ? null : type
    setOpeningFilter(newOpening)
    fetchNow(searchInput, positionFilter, golaFilter, newOpening, categoryGroupFilter, 0)
  }, [openingFilter, searchInput, positionFilter, golaFilter, categoryGroupFilter, fetchNow])

  const handleCategoryGroup = useCallback((key) => {
    const newGroup = categoryGroupFilter === key ? null : key
    setCategoryGroupFilter(newGroup)
    if (!newGroup && !searchInput && !positionFilter && !golaFilter && !openingFilter) {
      clearTimeout(debounceRef.current)
      setProducts([])
      setTotal(0)
      setHasSearched(false)
      setPage(0)
      return
    }
    fetchNow(searchInput, positionFilter, golaFilter, openingFilter, newGroup, 0)
  }, [categoryGroupFilter, searchInput, positionFilter, golaFilter, openingFilter, fetchNow])

  // ─── Pagination ──────────────────────────────────────────────
  const handlePageChange = useCallback((_, newPage) => {
    setPage(newPage)
    fetchProducts(searchInput, positionFilter, golaFilter, openingFilter, categoryGroupFilter, newPage, pageSize)
  }, [fetchProducts, searchInput, positionFilter, golaFilter, openingFilter, categoryGroupFilter, pageSize])

  const handlePageSizeChange = useCallback((e) => {
    const newSize = parseInt(e.target.value, 10)
    setPageSize(newSize)
    setPage(0)
    fetchProducts(searchInput, positionFilter, golaFilter, openingFilter, categoryGroupFilter, 0, newSize)
  }, [fetchProducts, searchInput, positionFilter, golaFilter, openingFilter, categoryGroupFilter])

  // ─── Clear all ───────────────────────────────────────────────
  const handleClearAll = useCallback(() => {
    setSearchInput("")
    setPositionFilter(null)
    setGolaFilter(false)
    setOpeningFilter(null)
    setCategoryGroupFilter(null)
    setProducts([])
    setTotal(0)
    setHasSearched(false)
    setPage(0)
  }, [])

  // ─── Refresh ─────────────────────────────────────────────────
  const handleRefresh = useCallback(() => {
    if (hasSearched) fetchProducts(searchInput, positionFilter, golaFilter, openingFilter, categoryGroupFilter, page, pageSize)
    if (analyticsFetched) fetchAnalytics()
  }, [hasSearched, searchInput, positionFilter, golaFilter, openingFilter, categoryGroupFilter, page, pageSize, analyticsFetched])

  // ─── Analytics ───────────────────────────────────────────────
  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true)
    try { setAnalyticsData(await fetchProductAnalytics()) } catch { /* ignore */ }
    setAnalyticsLoading(false)
  }, [])

  const stats = useMemo(() => analyticsData.stats, [analyticsData])

  const categoryChartData = useMemo(() => {
    const colors = ["#3b82f6", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444", "#6366f1", "#ec4899", "#14b8a6"]
    return Object.entries(analyticsData.categories || {})
      .map(([name, count], i) => ({ name, count, color: colors[i % colors.length] }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 12)
  }, [analyticsData.categories])

  const positionChartData = useMemo(() => {
    const pc = analyticsData.positionCounts || {}
    return [
      { name: "ΒΑΣΕΩΣ", count: pc["ΒΑΣΕΩΣ"] || 0, color: "#3b82f6" },
      { name: "ΚΡΕΜΑΣΤΟ", count: pc["ΚΡΕΜΑΣΤΟ"] || 0, color: "#8b5cf6" },
      { name: "ΟΛΟΣΩΜΟ", count: pc["ΟΛΟΣΩΜΟ"] || 0, color: "#10b981" },
    ]
  }, [analyticsData.positionCounts])

  const accessoryChartData = useMemo(() => {
    const colors = ["#f59e0b", "#ef4444", "#6366f1", "#ec4899", "#14b8a6", "#3b82f6"]
    return Object.entries(analyticsData.accessoryCounts || {})
      .map(([name, count], i) => ({ name, count, color: colors[i % colors.length] }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8)
  }, [analyticsData.accessoryCounts])

  const groupTotalsPieData = useMemo(() => {
    const g = analyticsData.groupTotals || {}
    return [
      { name: t("Boxes"), count: g.Boxes || 0, color: "#3b82f6" },
      { name: t("Accessories"), count: g.Accessories || 0, color: "#f59e0b" },
      { name: t("Doors"), count: g.Doors || 0, color: "#8b5cf6" },
      { name: t("Panels"), count: g.Panels || 0, color: "#10b981" },
      { name: t("Worktops"), count: g.Worktops || 0, color: "#ec4899" },
      { name: t("Decorative"), count: g.Decorative || 0, color: "#14b8a6" },
      { name: t("Other"), count: g.Other || 0, color: "#94a3b8" },
    ].filter(i => i.count > 0)
  }, [analyticsData.groupTotals, t])

  // ─── Helpers ─────────────────────────────────────────────────
  const getStockStyle = (stock) => {
    const s = Number(stock) || 0
    if (s <= 0) return { bgcolor: "#fee2e2", color: "#991b1b" }
    if (s < 10) return { bgcolor: "#fef3c7", color: "#92400e" }
    return { bgcolor: "#dcfce7", color: "#166534" }
  }

  const getUsageTypeConfig = (usageType) => {
    switch (usageType) {
      case "MATERIAL": return { label: t("Material"), bgcolor: "#dbeafe", color: "#1d4ed8", icon: <BuildIcon sx={{ fontSize: 12 }} /> }
      case "PURCHASED-MATERIAL": return { label: t("Purchased"), bgcolor: "#fef3c7", color: "#92400e", icon: <PrecisionManufacturingIcon sx={{ fontSize: 12 }} /> }
      case "COMPLETE-PRODUCT": return { label: t("Product"), bgcolor: "#dcfce7", color: "#166534", icon: <ShoppingCartIcon sx={{ fontSize: 12 }} /> }
      case "SERVICE": return { label: t("Service"), bgcolor: "#ede9fe", color: "#7c3aed", icon: <MiscellaneousServicesIcon sx={{ fontSize: 12 }} /> }
      case "COMPILED-PRODUCT": return { label: t("Compiled"), bgcolor: "#d1fae5", color: "#059669", icon: <LayersIcon sx={{ fontSize: 12 }} /> }
      default: return { label: usageType || "-", bgcolor: "#f3f4f6", color: "#6b7280", icon: null }
    }
  }

  const hasFilters = searchInput || positionFilter || golaFilter || openingFilter || categoryGroupFilter

  // ─── Render ──────────────────────────────────────────────────
  return (
    <Box sx={{ px: 1.5, py: 1.5, minHeight: "100vh" }}>
      {/* Header */}
      <Paper elevation={0} sx={{ px: 2, py: 1, mb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: "text.secondary" }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Breadcrumbs separator="/" sx={{ "& .MuiBreadcrumbs-separator": { mx: 0.5, color: "text.disabled" } }}>
            <Link component={RouterLink} to="/" sx={{ display: "flex", alignItems: "center", color: "text.secondary", textDecoration: "none", fontSize: "0.813rem" }}>
              <HomeIcon sx={{ fontSize: 16, mr: 0.5 }} />
            </Link>
            <Typography sx={{ fontSize: "0.813rem", fontWeight: 600, color: "text.primary", display: "flex", alignItems: "center" }}>
              <CategoryIcon sx={{ fontSize: 16, mr: 0.5, color: "primary.main" }} />
              {t("Products")}
            </Typography>
          </Breadcrumbs>
          {total > 0 && (
            <Chip label={`${total.toLocaleString()} ${t("results")}`} size="small" sx={{ ml: 1, height: 22, fontSize: "0.688rem" }} />
          )}
        </Stack>
        <IconButton size="small" onClick={handleRefresh} disabled={loading} sx={{ color: "text.secondary", width: 32, height: 32 }}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Paper>

      {/* Analytics (lazy) */}
      <Paper elevation={0} sx={{ mb: 1.5, borderRadius: 1, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        <Box
          onClick={() => {
            const willExpand = !analyticsExpanded
            setAnalyticsExpanded(willExpand)
            if (willExpand && !analyticsFetched) { setAnalyticsFetched(true); fetchAnalytics() }
          }}
          sx={{ px: 2, py: 0.75, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <BarChartIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography sx={{ fontSize: "0.813rem", fontWeight: 500, color: "text.primary" }}>{t("Analytics")}</Typography>
            {analyticsFetched && (
              <Typography sx={{ fontSize: "0.75rem", color: "text.disabled", ml: 2 }}>
                {stats.total || 0} {t("products")} · {formatCurrency(stats.totalValue || 0)} {t("stock value")}
              </Typography>
            )}
            {analyticsLoading && <CircularProgress size={14} sx={{ ml: 1 }} />}
          </Stack>
          <IconButton size="small" sx={{ p: 0.25 }}>
            {analyticsExpanded ? <ExpandLessIcon sx={{ fontSize: 20, color: "text.secondary" }} /> : <ExpandMoreIcon sx={{ fontSize: 20, color: "text.secondary" }} />}
          </IconButton>
        </Box>
        <Collapse in={analyticsExpanded}>
          <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: "0.813rem", color: "text.secondary" }}>{t("Total")}: <strong>{stats.total || 0}</strong></Typography>
              <Typography sx={{ fontSize: "0.813rem", color: "text.secondary" }}>{t("In Stock")}: <strong style={{ color: "#16a34a" }}>{stats.inStock || 0}</strong></Typography>
              <Typography sx={{ fontSize: "0.813rem", color: "text.secondary" }}>{t("Low Stock")}: <strong style={{ color: "#d97706" }}>{stats.lowStock || 0}</strong></Typography>
              <Typography sx={{ fontSize: "0.813rem", color: "text.secondary" }}>{t("Out of Stock")}: <strong style={{ color: "#dc2626" }}>{stats.outOfStock || 0}</strong></Typography>
              <Typography sx={{ fontSize: "0.813rem", color: "text.secondary", ml: "auto" }}>{t("Stock Value")}: <strong style={{ color: "#3b82f6" }}>{formatCurrency(stats.totalValue || 0)}</strong></Typography>
            </Stack>
            <Grid container spacing={2}>
              {/* Bar 1: All Categories */}
              <Grid item xs={12} md={3}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "text.secondary", mb: 1, textTransform: "uppercase" }}>{t("Categories")}</Typography>
                <Box sx={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={categoryChartData} layout="vertical" margin={{ top: 5, right: 10, left: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis type="number" tick={{ fontSize: 9, fill: "#6b7280" }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 9, fill: "#6b7280" }} width={80} />
                      <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4, border: "1px solid #e5e7eb" }} formatter={(v) => [v, t("Products")]} />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]}>{categoryChartData.map((e, i) => <Cell key={i} fill={e.color} />)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
              {/* Bar 2: Cabinet Positions */}
              <Grid item xs={12} md={3}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "text.secondary", mb: 1, textTransform: "uppercase" }}>{t("Cabinet Positions")}</Typography>
                <Box sx={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={positionChartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#6b7280" }} />
                      <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} />
                      <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4, border: "1px solid #e5e7eb" }} formatter={(v) => [v, t("Products")]} />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>{positionChartData.map((e, i) => <Cell key={i} fill={e.color} />)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
              {/* Bar 3: Accessories */}
              <Grid item xs={12} md={3}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "text.secondary", mb: 1, textTransform: "uppercase" }}>{t("Accessories")}</Typography>
                <Box sx={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={accessoryChartData} layout="vertical" margin={{ top: 5, right: 10, left: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis type="number" tick={{ fontSize: 9, fill: "#6b7280" }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 9, fill: "#6b7280" }} width={80} />
                      <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4, border: "1px solid #e5e7eb" }} formatter={(v) => [v, t("Products")]} />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]}>{accessoryChartData.map((e, i) => <Cell key={i} fill={e.color} />)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
              {/* Pie: Totals by Group */}
              <Grid item xs={12} md={3}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "text.secondary", mb: 1, textTransform: "uppercase" }}>{t("Totals")}</Typography>
                <Box sx={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={groupTotalsPieData} cx="50%" cy="50%" innerRadius={35} outerRadius={65} paddingAngle={2} dataKey="count" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                        {groupTotalsPieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                      </Pie>
                      <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4, border: "1px solid #e5e7eb" }} formatter={(v) => [v, t("Products")]} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Category Group Buttons */}
      <Paper elevation={0} sx={{ px: 2, py: 0.75, mb: 1.5, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          {CATEGORY_GROUPS.map((group) => {
            const isActive = categoryGroupFilter === group.key
            const Icon = group.icon
            return (
              <Chip
                key={group.key}
                icon={<Icon sx={{ fontSize: 15 }} />}
                label={t(group.label)}
                size="small"
                variant={isActive ? "filled" : "outlined"}
                onClick={() => handleCategoryGroup(group.key)}
                sx={{
                  height: 30,
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  ...(isActive
                    ? { bgcolor: "#1e293b", color: "#fff", "& .MuiChip-icon": { color: "#fff" } }
                    : { borderColor: "#cbd5e1", color: "#475569", "&:hover": { bgcolor: "#f1f5f9" }, "& .MuiChip-icon": { color: "#64748b" } }),
                }}
              />
            )
          })}
        </Stack>
      </Paper>

      {/* Search + Filters */}
      <Paper elevation={0} sx={{ px: 2, py: 0.75, mb: 1.5, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder={t("Search by name, SKU...")}
            value={searchInput}
            onChange={handleSearchInput}
            onKeyDown={(e) => e.key === "Enter" && searchInput.length >= 2 && fetchNow(searchInput, positionFilter, golaFilter, openingFilter, categoryGroupFilter, 0)}
            autoFocus
            sx={{
              minWidth: 220, flex: "0 1 260px",
              "& .MuiOutlinedInput-root": { height: 32, fontSize: "0.82rem", "& fieldset": { borderColor: "divider" } },
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} /></InputAdornment>,
              endAdornment: loading ? <CircularProgress size={14} sx={{ mr: 0.5 }} /> : null,
            }}
          />
          {["BASE", "WALL", "TALL"].map((pos) => (
            <Chip
              key={pos} label={pos} size="small"
              variant={positionFilter === pos ? "filled" : "outlined"}
              onClick={() => handlePositionFilter(pos)}
              sx={{ height: 24, fontSize: "0.7rem", fontWeight: 600, cursor: "pointer", ...(positionFilter === pos ? { bgcolor: "#3b82f6", color: "#fff" } : { borderColor: "#d1d5db", color: "#6b7280" }) }}
            />
          ))}
          <Chip
            label="GOLA" size="small"
            variant={golaFilter ? "filled" : "outlined"}
            onClick={handleGolaFilter}
            sx={{ height: 24, fontSize: "0.7rem", fontWeight: 600, cursor: "pointer", ...(golaFilter ? { bgcolor: "#7c3aed", color: "#fff" } : { borderColor: "#d1d5db", color: "#6b7280" }) }}
          />
          {["DRAWER", "DOOR", "OVEN", "SINK", "CORNER", "FRIDGE"].map((type) => (
            <Chip
              key={type} label={type} size="small"
              variant={openingFilter === type ? "filled" : "outlined"}
              onClick={() => handleOpeningFilter(type)}
              sx={{ height: 24, fontSize: "0.7rem", fontWeight: 500, cursor: "pointer", ...(openingFilter === type ? { bgcolor: "#059669", color: "#fff" } : { borderColor: "#d1d5db", color: "#6b7280" }) }}
            />
          ))}
          {hasFilters && (
            <IconButton size="small" onClick={handleClearAll} sx={{ color: "text.disabled", width: 24, height: 24 }}>
              <ClearIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
          {hasSearched && total > 0 && (
            <Typography sx={{ fontSize: "0.7rem", color: "text.disabled", ml: "auto", whiteSpace: "nowrap" }}>
              {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} / {total}
            </Typography>
          )}
        </Stack>
      </Paper>

      {/* Table or Empty State */}
      <Paper elevation={0} sx={{ borderRadius: 1, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        {!hasSearched && !loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
            <SearchIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }} />
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "text.secondary", mb: 0.5 }}>
              {t("Search for products")}
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "text.disabled" }}>
              {t("Type at least 2 characters or select a filter to start")}
            </Typography>
          </Box>
        ) : loading && products.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "text.secondary", textTransform: "uppercase", py: 1 }}>{t("SKU")}</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "text.secondary", textTransform: "uppercase", py: 1 }}>{t("Product")}</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "text.secondary", textTransform: "uppercase", py: 1 }}>{t("Type")}</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "text.secondary", textTransform: "uppercase", py: 1 }}>{t("Category")}</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "text.secondary", textTransform: "uppercase", py: 1 }}>{t("Dimensions")}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.7rem", color: "text.secondary", textTransform: "uppercase", py: 1 }}>{t("Stock")}</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "text.secondary", textTransform: "uppercase", py: 1 }}>{t("Unit")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.length > 0 ? products.map((product) => {
                    let photos = []
                    try { photos = JSON.parse(product.productPhotos || "[]") } catch { /* skip */ }
                    const hasDims = product.productDepth || product.productWidth || product.productHeight
                    const typeConfig = getUsageTypeConfig(product.usageType)
                    let cats = []
                    if (product.category) {
                      try { const p = JSON.parse(product.category); cats = Array.isArray(p) ? p : [product.category] } catch { cats = [product.category] }
                    }
                    let variantCount = 0
                    let variantTotalStock = 0
                    try { const v = JSON.parse(product.variants || "[]"); if (Array.isArray(v)) { variantCount = v.length; variantTotalStock = v.reduce((sum, vi) => sum + (parseInt(vi.stock) || 0), 0) } } catch { /* ignore */ }

                    return (
                      <TableRow key={product.id} hover onClick={() => navigate(`/product-overview/${product.id}`)} sx={{ cursor: "pointer" }}>
                        <TableCell sx={{ py: 1 }}>
                          <Typography sx={{ fontSize: "0.813rem", fontFamily: "monospace", color: "text.secondary" }}>{product.sku || "-"}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {photos.length > 0 ? (
                              <Box sx={{ width: 32, height: 32, borderRadius: 0.5, overflow: "hidden", flexShrink: 0, border: "1px solid", borderColor: "divider" }}>
                                <img src={photos[0]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              </Box>
                            ) : hasDims ? (
                              <Box sx={{ flexShrink: 0 }}><BoxDiagram length={Number(product.productDepth) || 0} height={Number(product.productHeight) || 0} width={Number(product.productWidth) || 0} size={32} productName={product.name} /></Box>
                            ) : null}
                            <Box>
                              <Typography sx={{ fontSize: "0.813rem", fontWeight: 500, color: "text.primary" }}>{product.name || "-"}</Typography>
                              {product.description && <Typography sx={{ fontSize: "0.65rem", color: "text.disabled", mt: 0.15, lineHeight: 1.3 }}>{product.description}</Typography>}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Chip icon={typeConfig.icon} label={typeConfig.label} size="small" sx={{ height: 22, fontSize: "0.7rem", fontWeight: 500, bgcolor: typeConfig.bgcolor, color: typeConfig.color, "& .MuiChip-icon": { color: typeConfig.color } }} />
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          {cats.length > 0 ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ gap: 0.5 }}>
                              {cats.map((cat, idx) => <Chip key={idx} label={cat} size="small" sx={{ height: 22, fontSize: "0.7rem", fontWeight: 500, bgcolor: "#f5f3ff", color: "#7c3aed" }} />)}
                            </Stack>
                          ) : <Typography sx={{ fontSize: "0.813rem", color: "text.disabled" }}>-</Typography>}
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          {variantCount > 0 ? (
                            <Chip label={`${variantCount} ${variantCount === 1 ? "size" : "sizes"}`} size="small" sx={{ fontSize: "0.7rem", height: 22, bgcolor: "#fce7f3", color: "#db2777", fontWeight: 600 }} />
                          ) : hasDims ? (
                            <Typography sx={{ fontSize: "0.75rem", fontFamily: "monospace", color: "text.secondary" }}>
                              {product.productDepth ? (product.productDepth / 10) : "-"}×{product.productWidth ? (product.productWidth / 10) : "-"}×{product.productHeight ? (product.productHeight / 10) : "-"} cm
                            </Typography>
                          ) : <Typography sx={{ fontSize: "0.813rem", color: "text.disabled" }}>-</Typography>}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          {variantCount > 0 ? (
                            <Chip label={`${variantTotalStock} (${variantCount} sizes)`} size="small" sx={{ height: 22, minWidth: 40, fontSize: "0.7rem", fontWeight: 600, ...getStockStyle(variantTotalStock) }} />
                          ) : (
                            <Chip label={Number(product.stock) || 0} size="small" sx={{ height: 22, minWidth: 40, fontSize: "0.7rem", fontWeight: 600, ...getStockStyle(product.stock) }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography sx={{ fontSize: "0.813rem", color: "text.secondary" }}>{product.unit || "-"}</Typography>
                        </TableCell>
                      </TableRow>
                    )
                  }) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <InventoryIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                        <Typography sx={{ fontSize: "0.875rem", color: "text.disabled" }}>{t("No products found matching your criteria")}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {total > 0 && (
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handlePageSizeChange}
                rowsPerPageOptions={[25, 50, 100]}
                labelRowsPerPage={t("Rows")}
                sx={{ borderTop: "1px solid", borderColor: "divider", "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "0.75rem" }, "& .MuiTablePagination-select": { fontSize: "0.75rem" } }}
              />
            )}
          </>
        )}
      </Paper>
    </Box>
  )
}

export default MuiProductsList
